import { LocalDate } from "@js-joda/core";
import { z } from "zod";
import { auth } from "../../core/auth";
import { AuthPermissionGuard } from "../../shared/components/AuthPermissionGuard";
import Page, { PageTab } from "../../shared/components/Page";
import usePathParams from "../../shared/hooks/usePathParams";
import { OfficeId, PatientId } from "../../shared/schema/schema";
import DailyNoteAttestationsTab from "./DailyNoteAttestationsRoute";
import DailyNotePatientView from "./DailyNotePatientView";
import DailyNotePatientsList from "./DailyNotePatientsList";
import DailyNoteDashboardTab from "./DailyNoteDashboardRoute";
import DailyNoteLicensesTab from "./DailyNoteLicensesRoute";
import DailyNoteSyncIssuesTab from "./DailyNoteSyncIssuesRoute";
import { ListPreset, dueDateRange } from "./dailyNotesShared";

// The row type the dashboard hands to the attestation modal.
export type { ListItem } from "./dailyNotesShared";

/**
 * The caregiver daily-note review.
 *
 * Notes arrive from FreedomCare's caregiver app after each visit, are triaged and classified by
 * background workers, and are signed off here in batches -- one patient at a time, because an
 * attestation covers one participant.
 *
 * Four tabs. "Dashboard" is the overview a nurse or manager lands on: the backlog, how well the
 * 14-day target is kept, and what failed to sync. "Sync issues" lists the notes whose DoCare
 * upload or Salesforce incident failed, with a way to send them again. The other two have two
 * grains of the data between them.
 *
 * "Daily notes" is the nurse's working queue, and it is patient-first: a list of patients with
 * notes waiting, each opening into that patient's own view (to review, all notes, attestations).
 * "Attestations" is one row per signed batch -- the record of what was attested and, where an
 * office requires it, the queue for the second reviewer. The same fact ("this batch is awaiting
 * a second signature") is true at both grains, which is why it read as duplication across two
 * pages rather than as one page zoomed two ways.
 *
 * Both tabs run off `view_visits_section_daily_notes`; only the co-sign action inside the batch
 * needs `cosign_visit_daily_note_attestation`. That is what makes one page workable -- before the
 * permissions were split, half of it would have been unreachable for the people who use the other
 * half.
 *
 * `Page.Tabbed` is lazy, so opening the page does not fetch both tabs.
 */
const tabs: PageTab[] = [
  {
    header: "Dashboard",
    route: "app.visits.dailyNoteOverview",
    component: <DailyNoteDashboardTab />,
  },
  {
    header: "Daily notes",
    route: "app.visits.dailyNotes",
    component: <DailyNotesTab />,
  },
  {
    header: "Attestations",
    route: "app.visits.dailyNoteAttestations",
    component: <DailyNoteAttestationsTab />,
  },
  {
    header: "Sync issues",
    route: "app.visits.dailyNoteSyncIssues",
    component: <DailyNoteSyncIssuesTab />,
  },
];

/**
 * Nurses' licenses, which decide who may sign. Offered only to whoever may manage them, the
 * same permission the server checks; everyone else does not see the tab.
 */
const licensesTab: PageTab = {
  header: "Licenses",
  route: "app.visits.dailyNoteLicenses",
  component: (
    <AuthPermissionGuard permission="manage_daily_note_licenses">
      <DailyNoteLicensesTab />
    </AuthPermissionGuard>
  ),
};

export default function DailyNotesRoute() {
  const visibleTabs = auth.isAllowedTo("manage_daily_note_licenses")
    ? [...tabs, licensesTab]
    : tabs;

  return (
    <AuthPermissionGuard permission="view_visits_section_daily_notes">
      <Page.Tabbed tabs={visibleTabs} />
    </AuthPermissionGuard>
  );
}

/**
 * Which patient and which view, from the URL.
 *
 * `notesPatient` present means the patient view; absent means the list. Held in the URL rather than
 * in component state so a link, a refresh and the browser's back button all land where the
 * nurse was.
 */
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullish()
  .catch(null);

const routeParams = z.object({
  notesPatient: z.coerce.number().int().positive().nullish(),
  notesView: z.enum(["review", "all", "attestations"]).nullish().catch(null),
  // The filters a Dashboard tile opens the list with; see `ListPreset`.
  notesDue: z.boolean().nullish().catch(null),
  notesFlagged: z.boolean().nullish().catch(null),
  notesFrom: isoDate,
  notesTo: isoDate,
  notesAnyDate: z.boolean().nullish().catch(null),
  notesOffice: z.coerce.number().int().positive().nullish().catch(null),
});

function DailyNotesTab() {
  const params = usePathParams(routeParams);

  if (params.notesPatient !== null && params.notesPatient !== undefined) {
    return (
      <DailyNotePatientView
        patientId={PatientId.parse(params.notesPatient)}
        view={params.notesView ?? "review"}
      />
    );
  }

  // Any of these params means the list was opened from a Dashboard tile.
  const isPreset =
    params.notesDue === true ||
    params.notesFlagged === true ||
    params.notesAnyDate === true ||
    !!params.notesFrom ||
    !!params.notesTo ||
    !!params.notesOffice;

  const dueRange = params.notesDue === true ? dueDateRange() : null;

  const preset: ListPreset | null = isPreset
    ? {
        dueOnly: params.notesDue === true,
        // The tiles' "concerning" link: the list opens with the Concerning filter on it.
        concerning: params.notesFlagged === true ? "CONCERNING" : null,
        officeId: params.notesOffice ? OfficeId.parse(params.notesOffice) : null,
        fromDate: params.notesFrom ? LocalDate.parse(params.notesFrom) : dueRange?.fromDate ?? null,
        toDate: params.notesTo ? LocalDate.parse(params.notesTo) : dueRange?.toDate ?? null,
      }
    : null;

  // Keyed on the preset so a different tile opens a fresh list: filters are read once, when
  // the list mounts, and would otherwise keep the previous tile's.
  return <DailyNotePatientsList key={JSON.stringify(preset)} preset={preset} />;
}
